import {responseToData, unRestangularize} from 'shared/utils/data-service-utils';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type {Category, FinishedSaveResponse, Language, Localization} from 'typings/portaro.be.types';
import type {AnyObject} from 'typings/portaro.fe.types';
import type {Department} from 'typings/portaro.be.types';

export default class LocalizationsDataService {
    public static serviceName = 'localizationsDataService';

    private readonly REQUEST_ROUTE = 'localizations';
    private readonly CATEGORIES_ROUTE = 'categories';
    private readonly LANGUAGES_ROUTE = 'languages';
    private readonly CREATE_ROUTE = 'create';

    /*@ngInject*/
    constructor(private Restangular: Restangular.IService) {
    }

    @ngAsync()
    @unRestangularize()
    public async query(filterParam: LocalizationsSearchParams): Promise<Localization[]> {
        try {
            return await this.Restangular
                .one(this.REQUEST_ROUTE)
                .getList(null, filterParam);
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async getCategories(): Promise<Category[]> {
        try {
            return await this.Restangular
                .one(this.REQUEST_ROUTE)
                .one(this.CATEGORIES_ROUTE)
                .getList();
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async getLanguages(): Promise<Language[]> {
        try {
            return await this.Restangular
                .one(this.REQUEST_ROUTE)
                .one(this.LANGUAGES_ROUTE)
                .getList();
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async createMessage(message: Localization | AnyObject): Promise<FinishedSaveResponse<Localization>> {
        try {
            return await this.Restangular
                .one(this.REQUEST_ROUTE)
                .one(this.CREATE_ROUTE)
                .post(null, message);
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async editMessage(message: Localization | AnyObject): Promise<FinishedSaveResponse<Localization>> {
        try {
            return await this.Restangular
                .one(this.REQUEST_ROUTE)
                .post(null, message);
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async deleteMessage(code: string, depId: number): Promise<void> {
        try {
            return await this.Restangular
                .one(this.REQUEST_ROUTE)
                .one(code)
                .one(depId.toString())
                .remove();
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async saveTranslation(translation): Promise<FinishedSaveResponse<Localization>> {
        try {
            return await this.Restangular
                .one(this.REQUEST_ROUTE)
                .one(translation.code)
                .one(translation.depId.toString())
                .one(translation.language)
                .post(null, {value: translation.value});
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async deleteTranslation(code: string, depId: number, language: string): Promise<void> {
        try {
            return await this.Restangular
                .one(this.REQUEST_ROUTE)
                .one(code)
                .one(depId.toString())
                .one(language)
                .remove();
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }
}

export interface LocalizationsSearchParams {
    category?: string;
    q?: string;
    code?: string;
    department?: Department;
}
